<template>
  <v-card outlined class="elevation-0">
    <v-card-title>Tài liệu</v-card-title>
    <v-card-text>
      <v-card-text class="d-flex justify-end">
        <v-btn color="primary" class="elevation-0 my-1 white--text" @click="upload" :disabled="!canUpload">
          <span>Upload tệp</span>
          <input ref="uploader" type="file" class="d-none" accept=".pdf, .xlsx, .doc, .docx" @change="onFileChanged" />
        </v-btn>
      </v-card-text>

      <v-data-table
        :headers="headers"
        :items="translatedDocuments"
        :loading="tableLoading"
        class="elevation-1"
        :items-per-page="pagination.itemsPerPage"
        :page.sync="pagination.page"
        :server-items-length="pagination.totalItems"
        :height="'250px'"
        :max-height="'250px'"
        fixed-header
        @pagination="updatePagination"
      >
        <template v-slot:[`item.title`]="{ item }">
          <div @click="item.file ? showFile(item) : null" style="color: #1a0dab; cursor: pointer">
            {{ getFileName(item) }}
          </div>
        </template>

        <template v-slot:[`item.createdAt`]="{ item }">
          {{ item.createdAt | ddmmyyyy }}
        </template>
        <template v-slot:[`item.status`]="{ item }">
          <v-chip
            small
            style="margin: 0"
            class="white--text"
            v-if="item.status"
            :color="item.status | getDocumentStatusColor"
            label
          >
            {{ item.status | getDocumentStatusText }}
          </v-chip>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon v-bind="attrs" v-on="on">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>

            <v-list>
              <v-list-item @click="showFile(item)">
                <v-list-item-title>Xem tài liệu</v-list-item-title>
              </v-list-item>

              <template v-if="canPerformActions">
                <v-list-item v-if="item.status !== 'Đã duyệt'" @click="() => approveDocument(item)">
                  <v-list-item-title>Duyệt</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="item.status !== 'Từ chối duyệt'" @click="denyDocument(item)">
                  <v-list-item-title>Từ chối</v-list-item-title>
                </v-list-item>
              </template>

              <template v-if="canDelete">
                <v-list-item @click="deleteDocument(item)">
                  <v-list-item-title>Xóa</v-list-item-title>
                </v-list-item>
              </template>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>

      <TeacherOfficeViewer :state="dialogState" :file="selectedFile" @close-dialog="dialogState = false" />
      <ApproveDialog ref="approve-dialog" @on-save="loadDocuments" />
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'
import TeacherOfficeViewer from '@/components/basic/office-viewer/TeacherOfficeViewer.vue'
import { get, some } from 'lodash'
import ApproveDialog from './ApproveDialog.vue'
import axios from 'axios'

export default {
  components: {
    TeacherOfficeViewer,
    ApproveDialog
  },

  props: {
    value: {
      type: Boolean,
      default: false
    },
    level: {
      type: String,
      default: ''
    },
    content: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      headers: [
        { text: 'Tên tài liệu', value: 'title', sortable: false },
        { text: 'Ngày tạo', value: 'createdAt', sortable: false },
        { text: 'Trạng thái', value: 'status', sortable: false },
        { text: 'Thao tác', value: 'actions', sortable: false }
      ],
      documents: [],
      dialogState: false,
      selectedFile: null,
      tableLoading: false,
      isSelecting: false,
      pagination: {
        page: 1,
        itemsPerPage: 10,
        totalItems: 0
      },
      statusMappings: {
        'not seen': 'Chưa duyệt',
        approved: 'Đã duyệt',
        denied: 'Từ chối duyệt'
      }
    }
  },
  computed: {
    ...mapGetters('auth', ['profile', 'role', 'user']),
    dialog: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    isLeader() {
      const subjectGroupConfig = get(this.profile, 'data.subjectGroupConfig', {})
      const currentLevel = this.level
      const currentSubjectGroupId = this.content.subjectGroupId

      return some(subjectGroupConfig, (groupId, key) => {
        const type = key.split('_')[2]
        return type === currentLevel && groupId === currentSubjectGroupId
      })
    },
    translatedDocuments() {
      return this.documents.map(doc => {
        return {
          ...doc
        }
      })
    },
    canViewDoc() {
      const isCurrTeacher = this.profile.id === this.content.currId

      switch (this.content.type) {
        case 'teacher':
          const canViewAllTeachers = this.$auth.policies['view-all-teacher-doc']
          return canViewAllTeachers || this.isLeader || isCurrTeacher

        case 'leader':
          const canViewLeader = this.$auth.policies['view-leader-doc']
          return canViewLeader || this.isLeader

        case 'ownClass':
          const canViewOwnClass = this.$auth.policies['view-ownClass-doc']
          return canViewOwnClass

        default:
          return false
      }
    },
    canPerformActions() {
      let isAllowedByPolicy

      switch (this.content.type) {
        case 'teacher':
          isAllowedByPolicy = this.$auth.policies['authorize-teacher-doc']
          return isAllowedByPolicy || this.isLeader

        case 'leader':
        case 'ownClass':
          isAllowedByPolicy = this.$auth.policies[`authorize-${this.content.type}-doc`]
          return isAllowedByPolicy

        default:
          return false
      }
    },
    canDelete() {
      let policyName

      switch (this.content.type) {
        case 'teacher':
          policyName = 'delete-teacher-doc'
          break
        case 'leader':
          policyName = 'delete-leader-doc'
          break
        case 'ownClass':
          policyName = 'delete-ownClass-doc'
          break
        default:
          policyName = ''
      }
      const isAllowedByPolicy = this.$auth.policies[policyName]

      const isCurrTeacher = this.profile.id === this.content.currId

      return isAllowedByPolicy || this.isLeader || isCurrTeacher
    },
    canUpload() {
      let policyName

      switch (this.content.type) {
        case 'teacher':
          policyName = 'upload-teacher-doc'
          break
        case 'leader':
          policyName = 'upload-leader-doc'
          break
        case 'ownClass':
          policyName = 'upload-ownClass-doc'
          break
        default:
          policyName = ''
      }
      const isAllowedByPolicy = this.$auth.policies[policyName]
      const isCurrTeacher = this.profile.id === this.content.currId
      return isAllowedByPolicy || this.isLeader || isCurrTeacher
    }
  },

  methods: {
    ...mapActions('teacherDoc', [
      'fetchTeacherDocs',
      'createTeacherDoc',
      'removeTeacherDoc',
      'updateTeacherDoc',
      'fetchTeacherDocCount'
    ]),
    ...mapActions('upload', ['uploadFile']),
    getFileName(item) {
      const isSigned = get(item, 'data.signFile', '').length > 0
      let result = get(item, 'file.name')
      if (isSigned) {
        result = result.replace('.docx', '.pdf')
        result += ' (Đã duyệt)'
      }
      return result
    },
    async loadDocuments() {
      this.tableLoading = true
      this.$loading.active = true
      const start = (this.pagination.page - 1) * this.pagination.itemsPerPage
      const limit = this.pagination.itemsPerPage
      let statusCondition = {}
      if (!this.canViewDoc) {
        statusCondition = { status_ne: 'not seen' }
      }

      try {
        this.pagination.totalItems = await this.fetchTeacherDocCount({ category_eq: this.content.catId })

        this.documents = await this.fetchTeacherDocs({
          category: this.content.catId,
          _start: start,
          _limit: limit,
          ...statusCondition
        })
      } catch (error) {
        console.error('Failed to load documents:', error)
      } finally {
        this.tableLoading = false
        this.$loading.active = false
      }
    },
    upload() {
      this.$refs.uploader.value = ''
      this.isSelecting = true
      window.addEventListener(
        'focus',
        () => {
          this.isSelecting = false
        },
        { once: true }
      )

      this.$refs.uploader.click()
    },
    async onFileChanged(e) {
      this.$loading.active = true
      const file = e.target.files[0]

      let createdDocument = {}
      try {
        const data = {
          category: this.content.catId,
          createdBy: this.profile.id,
          data: {
            title: file.name,
            url: null
          }
        }
        createdDocument = await this.createTeacherDoc(data)
        let formData = new FormData()
        formData.append('files', file)
        formData.append('refId', createdDocument.id)
        formData.append('ref', 'teacher-document')
        formData.append('field', 'file')

        const response = await this.uploadFile(formData)
        const uploadedFile = response[0]
        const updatedData = {
          data: {
            title: uploadedFile.name,
            url: uploadedFile.url
          }
        }

        await this.updateTeacherDoc({ id: createdDocument.id, data: updatedData })

        this.$alert.success('Tải file thành công')

        this.loadDocuments()
      } catch (error) {
        this.$alert.error('Tải file thất bại, vui lòng thử lại. Lỗi: ' + error)
        if (createdDocument.id) await this.removeTeacherDoc(createdDocument.id)
      } finally {
        this.$loading.active = false
      }
    },

    closeDialog() {
      this.$emit('input', false)
      this.documents = []
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      return moment(dateStr).format('DD/MM/YYYY')
    },
    showFile(item) {
      const file = item.file
      const signFileUrl = get(item, 'data.signFile', '')
      if (typeof signFileUrl === 'string' && signFileUrl.length) {
        this.selectedFile = { ...file, url: signFileUrl, name: file.name + '.pdf' }
      } else this.selectedFile = file
      this.dialogState = !this.dialogState
    },
    async approveDocument(item) {
      if (this.role.type === 'bod') {
        this.$loading.active = true
        try {
          let fileUrl = get(item, 'file.url', '')
          const ext = get(item, 'file.ext', '')
          if (ext !== '.pdf') {
            const filePresignUrl = get(item, 'data.filePresign', '')
            if (typeof filePresignUrl === 'string' && filePresignUrl.length) {
              fileUrl = filePresignUrl
            } else {
              const resp = await axios.post('https://docx2pdf.smoke-ant.com/convert', { url: fileUrl })
              fileUrl = get(resp, 'url')
              // await this.updateTeacherDoc({
              //   id: item.id,
              //   data: { 'data.filePresign': fileUrl }
              // })
            }
          }
          this.$refs['approve-dialog'].open(fileUrl, item.id)
        } catch (err) {
          console.error('err in approve document for bod', err)
        } finally {
          this.$loading.active = false
        }
      } else {
        this.tableLoading = true
        try {
          await this.updateTeacherDoc({
            id: item.id,
            data: { status: 'approved' }
          })
          this.$alert.success('Tài liệu đã được duyệt')
          this.loadDocuments()
        } catch (error) {
          this.$alert.error('Lỗi duyệt tài liệu: ' + error)
        } finally {
          this.tableLoading = false
        }
      }
    },

    async denyDocument(item) {
      this.tableLoading = true
      try {
        await this.updateTeacherDoc({
          id: item.id,
          data: { status: 'denied' }
        })
        this.$alert.success('Tài liệu đã bị từ chối')
        this.loadDocuments()
      } catch (error) {
        this.$alert.error('Lỗi từ chối tài liệu: ' + error)
      } finally {
        this.tableLoading = false
      }
    },

    async deleteDocument(item) {
      this.tableLoading = true
      try {
        await this.removeTeacherDoc(item.id)
        this.$alert.success('Tài liệu đã được xóa')
        this.loadDocuments()
      } catch (error) {
        this.$alert.error('Lỗi xóa tài liệu: ' + error)
      } finally {
        this.tableLoading = false
      }
    },
    updatePagination(val) {
      this.pagination.page = val.page
      this.pagination.itemsPerPage = val.itemsPerPage
    }
  },
  watch: {
    'pagination.page'() {
      this.loadDocuments()
    },
    'pagination.itemsPerPage'() {
      this.loadDocuments()
    },
    content(newVal) {
      if (newVal) {
        this.loadDocuments()
      }
    }
  }
}
</script>
